/**
 * 運費明細 + 月底運費歸單(REQ-20260908002)—— 前後端共用。
 *
 * ## 為什麼有這張表
 *
 * REQ-20260829003 讓出貨登記「選了貨運公司 + 填了寄送金額」就在同一個 transaction
 * 直接開一張 **POSTED** 的免稅運費進貨單(勾了已付款再順便沖帳)。使用者回報行不通:
 * 同一家貨運行一個月送 40 趟就有 40 張進貨單,對方只給**一張**月結對帳單,對不起來。
 *
 * 所以出貨端改成只寫一筆「運費明細」(`freight_lines`),**不建進貨單、不寫任何
 * 會計分錄**;月底在「運費歸單」畫面把同一家廠商的明細彙整成**一張 DRAFT 進貨單**,
 * 核對過對帳單再走既有的送出 / 確認狀態機入帳。
 *
 * ## ⚠️ 這不是 `purchase_order_lines` 的第二份真相
 *
 * REQ-20260907002 那句「沿用既有的 `purchase_order_lines`,不另開一張運費明細表」
 * **沒有被推翻**。分界是「時間」不是「內容」:
 *
 *  - `freight_lines` 是**歸單之前**的暫存事實 —— 出貨端寫入時**還沒有任何進貨單**,
 *    沒有單可以掛,不存在「掛在哪裡」的選擇。
 *  - 歸單時每一筆仍然轉成一列 `purchase_order_lines`;
 *    **「一張進貨單的金額從哪裡加總」仍然只有 `calcPurchaseTotals()` 一個答案**。
 *  - 歸單後 `status` 變 `CONSOLIDATED` 並回填 `purchaseOrderId` /
 *    `purchaseOrderLineId`,自己不再是金額來源,只剩來源軌跡的角色。
 */
import { z } from 'zod';
import { nonNegativeDecimalString } from './decimal.schema';

// ── 出貨端的寄送方式 ────────────────────────────────────────────────────

/**
 * 寄送方式(REQ-20260908002 圖一)。**必填**,而且只有 `CARRIER` 會展開
 * 貨運公司 / 託運單號 / 運費金額 / 付款方式那四格。
 *
 * ⚠️ DB 欄位是 nullable 的:功能上線前的既有出貨紀錄當時沒記這個維度,
 * **不回填猜測值**。「必填」只對**新建立**的出貨生效(zod + 前端)。
 */
export const SHIPPING_METHODS = {
  PICKUP: '客戶自取',
  SELF_DELIVERY: '我方自送',
  CARRIER: '委外貨運',
  OTHER: '其他',
} as const;
export type ShippingMethod = keyof typeof SHIPPING_METHODS;

// ── 運費明細的三個值域 ──────────────────────────────────────────────────

/**
 * 運費明細的狀態機。**只有三態**,而且轉換集中在 `nextFreightLineStatus()` ——
 * 散在各處各判一次就會出現「這裡當它還能歸單、那裡當它已經作廢」。
 *
 *   UNCONSOLIDATED(未歸單)→ CONSOLIDATED(已歸單 / 已入帳)
 *   UNCONSOLIDATED(未歸單)→ VOID(作廢:來源出貨作廢)
 *
 * `CONSOLIDATED` 是終點:帳已經做了,要改只能去把那張進貨單作廢
 * (既有功能,見進貨管理)。
 */
export const FREIGHT_LINE_STATUSES = {
  UNCONSOLIDATED: '未歸單',
  CONSOLIDATED: '已歸單',
  VOID: '已作廢',
} as const;
export type FreightLineStatus = keyof typeof FREIGHT_LINE_STATUSES;

/** 狀態機唯一的轉換表。key 是「從哪裡」,value 是「可以到哪些」 */
const FREIGHT_LINE_TRANSITIONS: Record<FreightLineStatus, readonly FreightLineStatus[]> = {
  UNCONSOLIDATED: ['CONSOLIDATED', 'VOID'],
  CONSOLIDATED: [],
  VOID: [],
};

/**
 * 唯一的狀態轉換入口。合法就回新狀態,不合法回 `null`(呼叫端決定要 400 還是略過)。
 *
 * 刻意**不丟例外**:出貨作廢時要把「未歸單的一併作廢、已歸單的原樣留著」,
 * 那是正常流程不是錯誤;丟例外的話呼叫端得先自己判一次,判斷邏輯就變成兩份。
 */
export function nextFreightLineStatus(
  from: FreightLineStatus,
  to: FreightLineStatus,
): FreightLineStatus | null {
  return FREIGHT_LINE_TRANSITIONS[from].includes(to) ? to : null;
}

/**
 * 歸屬工單的狀態(REQ-20260908002 圖一)。由**出貨端該張出貨對應的訂單張數**決定,
 * 使用者不填 —— 出貨端不處理分攤,只顯示提示。
 *
 *   NORMAL              1 張訂單 → `salesOrderNo` 直接寫入,歸單時直接入帳
 *   PENDING_ALLOCATION  多張訂單 → `salesOrderNo` 為 null,運費分攤在歸單時決定
 *   PENDING_DESTINATION 0 張訂單 → `salesOrderNo` 為 null,成本去向在歸單時指定
 */
export const FREIGHT_WORK_ORDER_STATES = {
  NORMAL: '正常',
  PENDING_ALLOCATION: '待分攤',
  PENDING_DESTINATION: '待指定去向',
} as const;
export type FreightWorkOrderState = keyof typeof FREIGHT_WORK_ORDER_STATES;

/**
 * 由「這張出貨對應到哪幾張工單」推出要寫進 `freight_lines` 的兩格。
 * **前後端共用同一支** —— 出貨端寫入時用它,彈窗上要顯示哪一種提示也用它,
 * 兩邊各判一次就會出現「後端存待分攤、畫面說已自動帶入」。
 *
 * 「工單」在這套系統裡就是出貨單號(`sales_orders.orderNo`,B 字軌),
 * 與運費進貨單那一格的值域逐字相同(REQ-20260905003 的 join table)。
 *
 * ⚠️ `shipments.orderId` 是**單一 FK**,所以從出貨端走到這裡今天永遠只有一張
 * → `NORMAL`。另外兩種**不是臆測性功能**:它們是歸單畫面與運費進貨單真正要處理
 * 的狀態(一列運費對到多張工單 → 待分攤;手動補登沒有工單 → 待指定去向),
 * 值域與判斷寫在同一支函式裡,將來出貨真的能跨單時不必再找第二處改。
 */
export function resolveFreightWorkOrder(salesOrderNos: readonly string[]): {
  salesOrderNo: string | null;
  workOrderState: FreightWorkOrderState;
} {
  if (salesOrderNos.length === 1) {
    return { salesOrderNo: salesOrderNos[0] ?? null, workOrderState: 'NORMAL' };
  }
  return {
    salesOrderNo: null,
    workOrderState: salesOrderNos.length === 0 ? 'PENDING_DESTINATION' : 'PENDING_ALLOCATION',
  };
}

/**
 * 付款方式三選一(REQ-20260908002 圖一)。取代 REQ-20260829003 的
 * 「☑ 運費已付款 + 現金 / 支票」——「這筆運費要不要變成應付」才是要問的問題,
 * 「用什麼工具付」是歸單之後在應付沖帳回答的。
 *
 * ⚠️ 值域與 `PURCHASE_LINE_PAYMENT_TYPES`(進貨明細的付款別)**刻意逐字相同** ——
 * 歸單時它就是原封不動寫進 `purchase_order_lines.linePaymentType`,
 * 換個拼法只會在兩邊之間多一層要對照的翻譯。
 */
export const FREIGHT_PAYMENT_TYPES = {
  MONTHLY: '月結應付',
  CASH: '當場付現',
  COLLECT: '到付 · 客戶付',
} as const;
export type FreightPaymentType = keyof typeof FREIGHT_PAYMENT_TYPES;

// ── API 型別 ────────────────────────────────────────────────────────────

/** 歸單畫面的一列。金額可能是 null(到付留白) */
export interface FreightLineItem {
  id: number;
  uuid: string;
  shipmentId: number;
  /** 來源出貨所屬的訂單 id(點「來源」回查用) */
  sourceOrderId: number;
  /** 來源出貨所屬的訂單單號(B 字軌)。⚠️ shipments 沒有自己的單號 */
  sourceOrderNo: string;
  customerId: number;
  customerSimpleName: string;
  vendorId: number;
  vendorSimpleName: string;
  salesOrderNo: string | null;
  workOrderState: string;
  trackingNo: string | null;
  /** 未稅金額。到付可能是 null */
  amount: string | null;
  paymentType: string;
  /** 運送日 YYYY-MM-DD(台北) */
  shipDate: string;
  status: string;
  purchaseOrderId: number | null;
  /** 已歸單時那張進貨單的單號(顯示用,join 出來不 freeze) */
  purchaseOrderNo: string | null;
  createdAt: string;
}

export interface FreightLineListResponse {
  items: FreightLineItem[];
  /** 未歸單且非到付的金額合計 —— 畫面上「全部未歸單合計」那一格 */
  totalAmount: string;
}

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD');

/**
 * POST /freight-lines/consolidate —— 把勾選的明細彙整成一張 DRAFT 進貨單。
 *
 * `statementAmount` 選填:對帳單還沒到、或先把這個月的明細收成一張草稿都是常態。
 * 稅別不在這裡帶 —— 由後端依 `suppliers.taxMode` 推導(見 REQUEST.md §Step 5),
 * 使用者要改是進表單改,不是在歸單畫面多問一次。
 */
export const ConsolidateFreightLinesSchema = z.object({
  vendorId: z.number().int().positive(),
  /** 帳單日期(= 進貨單的 txnDate,也決定 A 字軌單號的日期段) */
  txnDate: dateOnly,
  freightLineIds: z.array(z.number().int().positive()).min(1, '至少要勾選一筆運費明細'),
  statementNo: z.string().trim().max(30).optional().nullable(),
  statementAmount: nonNegativeDecimalString.optional().nullable(),
});
export type ConsolidateFreightLinesInput = z.infer<typeof ConsolidateFreightLinesSchema>;

export interface ConsolidateFreightLinesResponse {
  purchaseOrderId: number;
  orderNo: string;
  lineCount: number;
  /** 進貨單的含稅總計(到付的列不計入,見 calcPurchaseTotals) */
  total: string;
}

/**
 * PATCH /freight-lines/:id —— 只補「當時還不知道」的兩格。
 *
 * 刻意**不開放改廠商 / 客戶 / 運送日**:那幾格是出貨當下的事實,
 * 要改的話該改的是出貨紀錄本身(而出貨是 append-only,見 REQ-20260817007 §二)。
 */
export const PatchFreightLineSchema = z
  .object({
    trackingNo: z.string().trim().max(50).optional().nullable(),
    amount: nonNegativeDecimalString.optional().nullable(),
  })
  .refine(
    (v) => v.trackingNo !== undefined || v.amount !== undefined,
    '請至少填寫託運單號或金額其中一項',
  );
export type PatchFreightLineInput = z.infer<typeof PatchFreightLineSchema>;
